import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { atom_tourNotifications, TourNotification } from '@app/shared/state/notification.atom';
import { atom_tourState, atom_originalTourState, atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { service_tourService } from '@app/shared/services/tours.service';
import { queueNotificationUpdate } from '@app/shared/services/notification-offline.service';

// Icon mapping for notification icons
const NOTIFICATION_ICON_MAP: Record<string, string> = {
	gift: 'fa-solid fa-gift',
	info: 'fa-solid fa-circle-info',
	star: 'fa-solid fa-star',
	heart: 'fa-solid fa-heart',
	bell: 'fa-solid fa-bell',
	map: 'fa-solid fa-map-marker-alt',
	compass: 'fa-solid fa-compass',
	camera: 'fa-solid fa-camera',
	ticket: 'fa-solid fa-ticket',
	coffee: 'fa-solid fa-mug-hot',
	food: 'fa-solid fa-utensils',
	warning: 'fa-solid fa-triangle-exclamation',
	check: 'fa-solid fa-circle-check',
	discount: 'fa-solid fa-percent',
	time: 'fa-solid fa-clock',
	walk: 'fa-solid fa-person-walking',
};

interface NotificationCardProps {
	notification: TourNotification;
	tourColor: string;
	isRTL: boolean;
	onPrimaryAction: (notification: TourNotification) => void;
	onSecondaryAction: (notification: TourNotification) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
	notification,
	tourColor,
	isRTL,
	onPrimaryAction,
	onSecondaryAction,
}) => {
	const { t } = useTranslation();
	const iconClass = notification.icon ? NOTIFICATION_ICON_MAP[notification.icon] : 'fa-solid fa-circle-info';

	// Format timestamp
	const formatTime = (timestamp: { _seconds: number; _nanoseconds: number }) => {
		const date = new Date(timestamp._seconds * 1000);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMins / 60);
		const diffDays = Math.floor(diffHours / 24);

		if (diffMins < 1) return t('just_now') || 'Just now';
		if (diffMins < 60) return `${diffMins}m ago`;
		if (diffHours < 24) return `${diffHours}h ago`;
		if (diffDays < 7) return `${diffDays}d ago`;
		return date.toLocaleDateString();
	};

	const hasCTA = notification.notificationType === 'cta' && notification.ctaConfig;
	const primaryButtonText = notification.ctaConfig?.primaryButtonText;
	const secondaryButtonText = notification.ctaConfig?.secondaryButtonText;

	return (
		<Box
			sx={{
				display: 'flex',
				gap: 1.5,
				py: 2,
				borderBottom: '1px solid #E8E8E8',
			}}
		>
			{/* Icon Circle */}
			<Box
				sx={{
					width: 36,
					height: 36,
					borderRadius: '50%',
					bgcolor: tourColor,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flexShrink: 0,
					mt: 0.25,
				}}
			>
				<i
					className={iconClass}
					style={{ fontSize: '16px', color: '#fff' }}
					aria-hidden="true"
				/>
			</Box>

			{/* Content */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				{/* Title - only show if it's a CTA type or has a title different from content */}
				{notification.title && (
					<Typography
						sx={{
							fontWeight: 600,
							fontSize: '15px',
							color: '#1F313B',
							lineHeight: 1.4,
							mb: 0.25,
						}}
					>
						{notification.title}
					</Typography>
				)}

				{/* Content/Description */}
				<Typography
					component="div"
					sx={{
						fontSize: '14px',
						color: '#48526B',
						lineHeight: 1.5,
						mb: hasCTA ? 1.5 : 0.5,
						'& p': { margin: 0 },
						'& strong': { fontWeight: 600 },
					}}
					dangerouslySetInnerHTML={{ __html: notification.content }}
				/>

				{/* CTA Buttons */}
				{hasCTA && (primaryButtonText || secondaryButtonText) && (
					<Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
						{primaryButtonText && (
							<Button
								variant="contained"
								size="small"
								onClick={() => onPrimaryAction(notification)}
								sx={{
									bgcolor: tourColor,
									color: '#fff',
									borderRadius: '20px',
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '13px',
									px: 2,
									py: 0.5,
									minHeight: '32px',
									boxShadow: 'none',
									'&:hover': {
										bgcolor: tourColor,
										opacity: 0.9,
										boxShadow: 'none',
									},
								}}
							>
								{primaryButtonText}
							</Button>
						)}
						{secondaryButtonText && (
							<Button
								variant="text"
								size="small"
								onClick={() => onSecondaryAction(notification)}
								sx={{
									color: '#48526B',
									textTransform: 'none',
									fontWeight: 500,
									fontSize: '13px',
									px: 1.5,
									py: 0.5,
									minHeight: '32px',
									'&:hover': {
										bgcolor: 'rgba(0,0,0,0.04)',
									},
								}}
							>
								{secondaryButtonText}
							</Button>
						)}
					</Box>
				)}

				{/* Timestamp */}
				<Typography
					sx={{
						fontSize: '12px',
						color: '#999',
					}}
				>
					{formatTime(notification.createdAt)}
				</Typography>
			</Box>
		</Box>
	);
};

export const Notifications: React.FC = () => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'he';
	const isDesktop = useRecoilValue(atom_isDesktop);

	const [notifications, setNotifications] = useRecoilState(atom_tourNotifications);
	const tourState = useRecoilValue(atom_tourState);
	const originalTour = useRecoilValue(atom_originalTourState);
	const tourStage = useRecoilValue(atom_tourStage);
	const setSideBarState = useSetRecoilState(sideBarStateAtom);
	const setListMode = useSetRecoilState(atom_listModeState);
	const setOpenedStop = useSetRecoilState(atom_openedStopState);
	const setActiveStop = useSetRecoilState(atom_activeStopState);
	const { markAsSeen } = useNotificationTrigger();

	const tourColor = tourState?.config?.mainColor || '#D0552E';

	// Track which notifications have been marked as seenOnList this session (to avoid duplicate API calls)
	const markedSeenOnListRef = useRef<Set<string>>(new Set());

	// Get credentials safely (only once)
	const credentials = useMemo(() => {
		try {
			return JSON.parse(localStorage.getItem('credential_items') || '{}');
		} catch {
			return {};
		}
	}, []);

	// Mark notifications as seenOnList when component opens
	useEffect(() => {
		// Safety checks
		if (tourStage !== 'map') return;
		if (!notifications || !Array.isArray(notifications)) return;

		const tourId = tourState?.id;
		const uID = credentials?.uID;
		if (!tourId || !uID) return;

		// Find notifications that need to be marked as seenOnList
		const unseenOnList = notifications.filter(
			n => n.showInList &&
				n.userState?.seen &&
				!n.userState?.seenOnList &&
				!markedSeenOnListRef.current.has(n.id)
		);

		if (unseenOnList.length === 0) return;

		// Track IDs to prevent duplicate API calls
		const idsToMark = unseenOnList.map(n => n.id);
		idsToMark.forEach(id => markedSeenOnListRef.current.add(id));
		// Update local Recoil state immediately for UI
		setNotifications(prev => {
			if (!prev || !Array.isArray(prev)) return prev;
			return prev.map(notif =>
				idsToMark.includes(notif.id)
					? { ...notif, userState: { ...notif.userState, seenOnList: true } }
					: notif
			);
		});

		// Always queue first for safety (handles deduplication internally)
		unseenOnList.forEach(n => {
			queueNotificationUpdate(tourId, n.id, 'seenOnList');
		});

		// Then try to sync immediately if online
		if (navigator.onLine) {
			const updates = unseenOnList.map(n => ({
				notificationId: n.id,
				action: 'seenOnList' as const,
				timestamp: Date.now(),
			}));

			service_tourService.batchUpdateNotificationState(tourId, uID, updates)
				.then(result => {
				})
				.catch(err => {
					console.warn('[Notifications] Failed to batch update seenOnList (queued for retry):', err);
				});
		}
	}, [tourStage, tourState?.id, credentials?.uID, notifications, setNotifications]);

	// Exit notifications
	const handleClose = useCallback(() => {
		setSideBarState(null);
		if (isDesktop) {
			setListMode('desktop');
		} else {
			setListMode('mobileSwiper');
		}
	}, [setSideBarState, setListMode, isDesktop]);

	// Handle primary CTA action (e.g., "Take a Look", "I'm Stopping")
	const handlePrimaryAction = useCallback((notification: TourNotification) => {
		markAsSeen(notification.id, 'ctaClicked');

		if (notification.ctaConfig) {
			const { actionType, actionValue } = notification.ctaConfig;

			if (actionType === 'link' && actionValue) {
				window.open(actionValue, '_blank');
				handleClose();
			} else if ((actionType === 'navigate' || actionType === 'modal') && actionValue) {
				const stop = originalTour?.stops?.find(s => s.id === actionValue);
				if (stop) {
					if (actionType === 'navigate') {
						setActiveStop(stop);
						setListMode('mobileNavigation');
					} else {
						setOpenedStop(stop);
					}
					setSideBarState(null);
					return;
				}
			}
		}

		handleClose();
	}, [markAsSeen, originalTour?.stops, setActiveStop, setListMode, setOpenedStop, setSideBarState, handleClose]);

	// Handle secondary action (e.g., "Skip for now", "Continue Walking")
	const handleSecondaryAction = useCallback((notification: TourNotification) => {
		markAsSeen(notification.id, 'ctaSkipped');
		// Just dismiss, don't take any action
	}, [markAsSeen]);

	// Don't render until tour stage is 'map'
	if (tourStage !== 'map') {
		return null;
	}

	// Safety check for notifications array
	if (!notifications || !Array.isArray(notifications)) {
		return null;
	}

	// Filter notifications: only show those with showInList=true and that have been seen
	const listNotifications = notifications.filter(n => n.showInList && n.userState?.seen);

	// Sort by date (newest first)
	const sortedNotifications = [...listNotifications].sort((a, b) => {
		return b.createdAt._seconds - a.createdAt._seconds;
	});

	const unreadCount = 0; // All shown notifications are already seen

	// Desktop view - side panel
	if (isDesktop) {
		return (
			<Box
				dir={isRTL ? 'rtl' : 'ltr'}
				sx={{
					width: '100%',
					height: '100%',
					bgcolor: 'white',
					display: 'flex',
					flexDirection: 'column',
					borderRight: '1px solid #e0e0e0',
				}}
			>
				{/* Header */}
				<Box sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					px: 2,
					py: 2,
					bgcolor: '#fff',
					borderBottom: '1px solid #e0e0e0',
				}}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
							{t('notifications') || 'Notifications'}
						</Typography>
						{unreadCount > 0 && (
							<Box
								sx={{
									bgcolor: tourColor,
									color: '#fff',
									borderRadius: '12px',
									px: 1,
									py: 0.25,
									fontSize: '12px',
									fontWeight: 600,
								}}
							>
								{unreadCount}
							</Box>
						)}
					</Box>
					<IconButton
						onClick={handleClose}
						aria-label={t('close') as string || 'Close'}
						size="small"
					>
						<i className="fa-regular fa-xmark" style={{ fontSize: '18px', color: '#666' }} />
					</IconButton>
				</Box>

				{/* List */}
				<Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
					{sortedNotifications.length === 0 ? (
						<Box sx={{ textAlign: 'center', py: 6 }}>
							<i
								className="fa-regular fa-bell-slash"
								style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px', display: 'block' }}
							/>
							<Typography sx={{ color: '#999', fontSize: '14px' }}>
								{t('no_notifications') || 'No notifications yet'}
							</Typography>
						</Box>
					) : (
						sortedNotifications.map((notification) => (
							<NotificationCard
								key={notification.id}
								notification={notification}
								tourColor={tourColor}
								isRTL={isRTL}
								onPrimaryAction={handlePrimaryAction}
								onSecondaryAction={handleSecondaryAction}
							/>
						))
					)}
				</Box>
			</Box>
		);
	}

	// Mobile view - full screen page
	return (
		<Box
			dir={isRTL ? 'rtl' : 'ltr'}
			sx={{
				width: '100%',
				minHeight: '100vh',
				maxWidth: 1200,
				margin: '0 auto',
				bgcolor: '#fff',
			}}
			component="section"
			role="region"
			aria-label={t('notifications') as string}
		>
			{/* Header */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: 2,
					bgcolor: '#fff',
					borderBottom: '1px solid #E8E8E8',
					position: 'sticky',
					top: 0,
					zIndex: 10,
				}}
			>
				<Box
					component="button"
					onClick={handleClose}
					aria-label={t('on_tour.go_back_tour') as string}
					sx={{
						width: 36,
						height: 36,
						borderRadius: '50%',
						background: '#F6F6F9',
						boxShadow: '0 2px 6px rgba(0,0,0,0.16)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
						border: 'none',
						padding: 0,
						transition: 'transform 0.2s ease',
						'&:hover': { transform: 'scale(0.9)' },
						'&:active': { transform: 'scale(0.85)' },
					}}
				>
					<svg width={20} height={20} viewBox="0 0 24 24" fill="#333" aria-hidden="true">
						<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
					</svg>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography variant="h1" sx={{ fontWeight: 700, fontSize: '20px' }}>
						{t('notifications') || 'Notifications'}
					</Typography>
					{unreadCount > 0 && (
						<Box
							sx={{
								bgcolor: tourColor,
								color: '#fff',
								borderRadius: '12px',
								px: 1,
								py: 0.25,
								fontSize: '12px',
								fontWeight: 600,
							}}
						>
							{unreadCount}
						</Box>
					)}
				</Box>

				<Box sx={{ width: 36 }} />
			</Box>

			{/* List */}
			<Box sx={{ px: 2, pb: '100px' }}>
				{sortedNotifications.length === 0 ? (
					<Box sx={{ textAlign: 'center', py: 8 }}>
						<i
							className="fa-regular fa-bell-slash"
							style={{ fontSize: '64px', color: '#ccc', marginBottom: '24px', display: 'block' }}
						/>
						<Typography sx={{ color: '#999', fontSize: '16px' }}>
							{t('no_notifications') || 'No notifications yet'}
						</Typography>
					</Box>
				) : (
					sortedNotifications.map((notification) => (
						<NotificationCard
							key={notification.id}
							notification={notification}
							tourColor={tourColor}
							isRTL={isRTL}
							onPrimaryAction={handlePrimaryAction}
							onSecondaryAction={handleSecondaryAction}
						/>
					))
				)}
			</Box>
		</Box>
	);
};

// Re-export for backward compatibility with existing imports
export const DesktopNotificationsContent = Notifications;
export const MobileNotificationsContent = Notifications;
